import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button, Card, Col, Dropdown, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import { getSize } from 'helpers/utils';
import Flex from 'components/common/Flex';
import CardDropdown from 'components/common/CardDropdown';
import cloudUpload from 'assets/img/icons/cloud-upload.svg';
import { toast } from 'react-toastify';
import csvtojson from 'csvtojson';
import * as XLSX from 'xlsx';
import { useAuth } from 'nursd-dashboard/context/AuthContext';
import { useEffect } from 'react';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import useAdvanceTable from 'hooks/useAdvanceTable';
import {
  getAllNurseOrgList,
  getOrganizationDetails
} from 'nursd-dashboard/api/organizationTable';
import { createNurse } from 'nursd-dashboard/api/nurseTable';
import moment from 'moment';
import IconButton from 'components/common/IconButton';

function FileUploadNurse() {
  const { authToken, location, organization, userId } = useAuth();
  const {
    handleSubmit,
    setValue,
    formState: { errors },
    clearErrors
  } = useForm();
  const [files, setFiles] = useState([]);
  const [orgLocationIdData, setOrgLocationIdData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [focus, setFocus] = useState('');

  const [emptyFields, setEmptyFields] = useState('');
  const [validateTable, setValidateTable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orgLocationIdAddress, setOrgLocationIdAddress] = useState([]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx'
      ]
    },
    onDrop: acceptedFiles => {
      if (errors.uploadedFiles) {
        clearErrors('uploadedFiles');
      }
      setValue('uploadedFiles', acceptedFiles);
      setFiles([
        ...files,
        ...acceptedFiles.map(file =>
          Object.assign(file, {
            preview: URL.createObjectURL(file)
          })
        )
      ]);
    }
  });

  const handleRemove = path => {
    const filteredFiles = files.filter(file => file.path !== path);
    setFiles(filteredFiles);
    setValue('uploadedFiles', filteredFiles.length ? filteredFiles : null);
    setTableData([]);
    setValidateTable(false);
    setEmptyFields('');
  };

  const onSubmit = () => {
    if (files.length === 0) {
      toast.warning('Upload the file to submit', {
        theme: 'colored'
      });
    } else {
      const file = files[0];
      const reader = new FileReader();
      reader.onload = async event => {
        const data = event.target.result;
        let parsedData = [];

        if (file.name.endsWith('.csv')) {
          parsedData = await csvtojson().fromString(data);
        } else if (file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          parsedData = XLSX.utils.sheet_to_json(sheet);
        }

        const column = Object.keys(parsedData[0]).map(key => {
          return { Header: key, accessor: key };
        });

        //Checking Unique Object
        let uniqueCSVFileArray = parsedData?.filter((item, index, array) => {
          return (
            array?.findIndex(element => {
              return (
                element.emailId === item.emailId ||
                element.phoneNumber === item.phoneNumber ||
                element.clinicalLicenseNumber === item.clinicalLicenseNumber
              );
            }) === index
          );
        });

        // console.log('parsedData', parsedData);
        // console.log('uniqueCSVFileArray', uniqueCSVFileArray);
        // console.log('columns', columns);

        //Checking Duplicate Object in DB
        const finalArray = uniqueCSVFileArray?.filter(item => {
          return !orgLocationIdData?.some(serverItem => {
            return (
              serverItem.emailId === item.emailId ||
              serverItem.phoneNumber === item.phoneNumber ||
              Number(serverItem.clinicalLicenseNumber) ===
                item.clinicalLicenseNumber
            );
          });
        });

        // console.log('finalArray', finalArray);

        const headerSpellFixArray = finalArray.map(item =>
          column?.reduce((acc, curr) => {
            const accessor = curr.accessor.replace(/\s+/g, '_').toLowerCase();
            acc[accessor] = item[curr.accessor] || '';
            return acc;
          }, {})
        );

        // console.log('headerSpellFixArray', headerSpellFixArray);

        setTableData(headerSpellFixArray);

        toast.info(
          'File with duplicate records automatically terminates the rows, and upload unique records.',
          {
            theme: 'colored'
          }
        );
      };
      reader.readAsBinaryString(file);
    }
  };

  const field = [
    'FirstName',
    'LastName',
    'FullAddress',
    'Gender',
    'EmailId',
    'PhoneNumber',
    'DateOfBirth',
    'PrimaryLicenseType',
    'PrimaryLicenseMultiPrivilege',
    'clinicalLicenseNumber',
    'clinicalLicenseExpirationDate',
    'Password'
  ];

  const getNurse = async () => {
    try {
      const facilityList = await getAllNurseOrgList(
        authToken,
        location,
        organization
      );

      const list = facilityList.sort((a, b) =>
        a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase())
      );
      setOrgLocationIdData(list);
    } catch (error) {
      console.log(error);
      setOrgLocationIdData([]);
    }
  };

  const getOrgLocationList = async () => {
    await getOrganizationDetails(authToken, userId)
      .then(res => {
        let arrState = [];
        if (res) {
          res?.locations.map(i =>
            arrState.push({
              location: i.location_id,
              state: i.location_state
            })
          );
          if (res?.locations.length === arrState.length) {
            setOrgLocationIdAddress(arrState);
          }
        }
      })
      .catch(e => {
        console.log(e);
      });
  };

  useEffect(() => {
    if (location !== '') {
      getNurse();
      getOrgLocationList();
    }
  }, [authToken, location]);

  const handleInputChange = (event, index) => {
    const { name, value } = event.target;
    const newData = [...tableData];
    newData[index][name] = value;
    setTableData(newData);
  };

  const validateFields = () => {
    let isValid = true;
    const newData = [...tableData];
    const invalidRows = [];
    const emptyFields = [];

    for (let i = 0; i < newData.length; i++) {
      const data = newData[i];
      const firstName = data?.firstname?.toString().trim();
      const lastName = data?.lastname?.toString().trim();
      const address = data?.fulladdress?.toString().trim();
      const gender = data?.gender?.toString().trim();
      const phone = data?.phonenumber?.toString().trim();
      const dateOfBirth = data?.dateofbirth?.toString().trim();
      const primaryLicenseType = data?.primarylicensetype?.toString().trim();
      const primaryLicenseMultiPrivilege = data?.primarylicensemultiprivilege
        ?.toString()
        .trim();
      const password = data?.password?.toString().trim();
      const email = data?.emailid?.toString().trim();

      if (
        firstName === '' ||
        lastName === '' ||
        address === '' ||
        primaryLicenseType === '' ||
        primaryLicenseMultiPrivilege === '' ||
        password === '' ||
        email === '' ||
        dateOfBirth === '' ||
        gender === '' ||
        phone === ''
      ) {
        isValid = false;
        invalidRows.push(data);
      }
    }

    for (let i = 0; i < newData.length; i++) {
      const firstName = newData[i]['firstname'];
      const lastName = newData[i]['lastname'];
      const address = newData[i]['fulladdress'];
      const gender = newData[i]['gender'];
      const phone = newData[i]['phonenumber'];
      const dateOfBirth = newData[i]['dateofbirth'];
      const primaryLicenseType = newData[i]['primarylicensetype'];
      const primaryLicenseMultiPrivilege =
        newData[i]['primarylicensemultiprivilege'];
      const password = newData[i]['password'];
      const email = newData[i]['emailid'];
      if (
        firstName === '' ||
        lastName === '' ||
        address === '' ||
        primaryLicenseType === '' ||
        primaryLicenseMultiPrivilege === '' ||
        password === '' ||
        email === '' ||
        dateOfBirth === '' ||
        gender === '' ||
        phone === ''
      ) {
        isValid = false;
        emptyFields.push(
          `firstname_${i}`,
          `lastname_${i}`,
          `fulladdress_${i}`,
          `gender_${i}`,
          `phonenumber_${i}`,
          `dateofbirth_${i}`,
          `primarylicensetype_${i}`,
          `primarylicensemultiprivilege_${i}`,
          `password_${i}`,
          `emailid_${i}`
        );
      }
    }

    setEmptyFields(emptyFields);

    if (emptyFields.length === 0) {
      setValidateTable(true);
    } else {
      setValidateTable(false);
    }

    return { isValid, invalidRows };
  };

  const columns = [
    {
      accessorKey: 'emailid',
      header: 'Email',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { emailid } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`emailid_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `emailid_${index}`}
            type="text"
            name="emailid"
            value={emailid}
            onChange={event => {
              setFocus(`emailid_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    },
    {
      accessorKey: 'password',
      header: 'Password',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { password } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`password_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `password_${index}`}
            type="text"
            name="password"
            value={password}
            onChange={event => {
              setFocus(`password_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    },
    {
      accessorKey: 'firstname',
      header: 'FirstName',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { firstname } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`firstname_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `firstname_${index}`}
            type="text"
            name="firstname"
            value={firstname}
            onChange={event => {
              setFocus(`firstname_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    },
    {
      accessorKey: 'lastname',
      header: 'LastName',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { lastname } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`lastname_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `lastname_${index}`}
            type="text"
            name="lastname"
            value={lastname}
            onChange={event => {
              setFocus(`lastname_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    },
    {
      accessorKey: 'phonenumber',
      header: 'Phone Number',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { phonenumber } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`phonenumber_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `phonenumber_${index}`}
            type="text"
            name="phonenumber"
            value={phonenumber}
            onChange={event => {
              setFocus(`phonenumber_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    },
    {
      accessorKey: 'gender',
      header: 'Gender',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { gender } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`gender_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `gender_${index}`}
            type="text"
            name="gender"
            value={gender}
            onChange={event => {
              setFocus(`gender_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    },
    {
      accessorKey: 'dateofbirth',
      header: 'Date of Birth',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { dateofbirth } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`dateofbirth_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `dateofbirth_${index}`}
            type="text"
            name="dateofbirth"
            value={dateofbirth}
            onChange={event => {
              setFocus(`dateofbirth_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    },
    {
      accessorKey: 'primarylicensetype',
      header: 'PrimaryLicenseType',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { primarylicensetype } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`primarylicensetype_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `primarylicensetype_${index}`}
            type="text"
            name="primarylicensetype"
            value={primarylicensetype}
            onChange={event => {
              setFocus(`primarylicensetype_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    },
    {
      accessorKey: 'primarylicensemultiprivilege',
      header: 'PrimaryLicenseMultiPrivilege',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { primarylicensemultiprivilege } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`primarylicensemultiprivilege_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `primarylicensemultiprivilege_${index}`}
            type="text"
            name="primarylicensemultiprivilege"
            value={primarylicensemultiprivilege}
            onChange={event => {
              setFocus(`primarylicensemultiprivilege_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    },
    {
      accessorKey: 'clinicallicensenumber',
      header: 'ClinicalLicenseNumber',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { clinicallicensenumber } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`clinicallicensenumber_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `clinicallicensenumber_${index}`}
            type="text"
            name="clinicallicensenumber"
            value={clinicallicensenumber}
            onChange={event => {
              setFocus(`clinicallicensenumber_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    },
    {
      accessorKey: 'clinicallicenseexpirationdate',
      header: 'ClinicalLicenseExpirationDate',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original, index } }) => {
        const { clinicallicenseexpirationdate } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`clinicallicenseexpirationdate_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `clinicallicenseexpirationdate_${index}`}
            type="text"
            name="clinicallicenseexpirationdate"
            value={clinicallicenseexpirationdate}
            onChange={event => {
              setFocus(`clinicallicenseexpirationdate_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    },
    {
      accessorKey: 'fulladdress',
      header: 'FullAddress',
      meta: {
        headerProps: { className: 'text-900' }
        // cellProps: { className: 'ps-5' }
      },
      cell: ({ row: { original, index } }) => {
        const { fulladdress } = original;
        return (
          <input
            required
            style={
              emptyFields.includes(`fulladdress_${index}`)
                ? {
                    border: '1px solid red',
                    padding: '5px',
                    outline: '1px solid red',
                    borderRadius: '5px'
                  }
                : {
                    border: '1px solid #ccc',
                    padding: '5px',
                    outline: '1px solid #ccc',
                    borderRadius: '5px'
                  }
            }
            autoFocus={focus === `fulladdress_${index}`}
            type="text"
            name="fulladdress"
            value={fulladdress}
            onChange={event => {
              setFocus(`fulladdress_${index}`);
              handleInputChange(event, index);
            }}
          />
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: tableData,
    columns,
    selection: true,
    sortable: true,
    pagination: true,
    perPage: 10
  });

  const postNurse = async (
    firstName,
    lastName,
    fullAddress,
    latitude,
    longitude,
    gender,
    email,
    phoneNumber,
    dateOfBirth,
    primaryLicenseType,
    primaryLicenseMultiPrivilege,
    clinicalLicenseNumber,
    clinicalLicenseExpirationDate,
    emailVerified,
    password,
    primaryState
  ) => {
    try {
      const item = await createNurse(authToken, {
        email: email,
        firstName: firstName,
        lastName: lastName,
        password: password,
        phoneNumber: phoneNumber,
        gender: gender,
        dateOfBirth: dateOfBirth,
        primaryState: primaryState,
        fullAddress: fullAddress,
        latitude: latitude.toString(),
        longitude: longitude.toString(),
        location_id: location,
        organization: organization,
        primaryLicenseType: primaryLicenseType,
        primaryLicenseMultiPrivilege: primaryLicenseMultiPrivilege,
        clinicalLicenseNumber: clinicalLicenseNumber,
        clinicalLicenseExpirationDate: clinicalLicenseExpirationDate,
        nurseVerified: emailVerified,
        emailVerified: emailVerified
      });
      return { item: item, code: 200 };
    } catch (error) {
      return { item: email, code: 401, error: error };
    }
  };

  const deepEqual = (obj1, obj2) => {
    if (obj1 === obj2) {
      return true;
    }

    if (
      typeof obj1 !== 'object' ||
      obj1 === null ||
      typeof obj2 !== 'object' ||
      obj2 === null
    ) {
      return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      // eslint-disable-next-line no-prototype-builtins
      if (!obj2?.hasOwnProperty(key)) {
        return false;
      }

      if (!deepEqual(obj1[key], obj2[key])) {
        return false;
      }
    }

    return true;
  };

  const getAddress = async data => {
    // console.log(data)
    let address = data;

    const GOOGLE_PLACES_API_KEY = 'AIzaSyASXZSrsvL25WiY7nZc2M4WJhkDMj-jkvs';
    const apiUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
      address
    )}&key=${GOOGLE_PLACES_API_KEY}`;

    try {
      const response = await fetch(apiUrl);
      const data = await response.json();
      let arr = [];
      data.results[0].address_components?.map(i => arr.push(i?.long_name));
      let full_address = arr?.join(',');
      let obj = {
        fullAddress: full_address,
        latitude: data.results[0].geometry.location.lat,
        longitude: data.results[0].geometry.location.lng
      };

      return obj;
    } catch (error) {
      console.error('Error:', error);
      return null;
    }
  };

  const upload = () => {
    const { isValid, invalidRows } = validateFields();

    const validRows = tableData.filter(data => {
      // Check if the row object is not present in the invalidRows array
      return !invalidRows.some(invalidRow => deepEqual(invalidRow, data));
    });

    if (isValid) {
      setLoading(true);
      // console.log('validRows', validRows);
      const postNursePromise = data => {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve, reject) => {
          // console.log(data)

          const inputDate = data?.dateofbirth;
          const inputDateclinicallicenseexpirationdate =
            data?.clinicallicenseexpirationdate;
          const outputFormat = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]';

          let address = await getAddress(data?.fulladdress?.toString());
          let state = orgLocationIdAddress?.find(i => i?.location === location);
          postNurse(
            data?.firstname?.toString(),
            data?.lastname?.toString(),
            address?.fullAddress?.toString(),
            address?.latitude?.toString(),
            address?.longitude?.toString(),
            data?.gender?.toString(),
            data?.emailid?.toString(),
            data?.phonenumber?.toString(),
            moment(inputDate, 'MM-DD-YYYY').format(outputFormat),
            data?.primarylicensetype?.toString(),
            data?.primarylicensemultiprivilege?.toLowerCase() === 'yes'
              ? true
              : false,
            data?.clinicallicensenumber?.toString(),
            moment(inputDateclinicallicenseexpirationdate, 'MM-DD-YYYY').format(
              outputFormat
            ),
            true,
            data?.password?.toString(),
            state?.state
          )
            .then(res => {
              // console.log(res);
              resolve(res); // Resolve the promise with the response
            })
            .catch(error => {
              console.error('error', error);
              reject(error); // Reject the promise with the error
            });
        });
      };

      // console.log(validRows?.length);
      Promise.all(validRows.map(postNursePromise))
        .then(res => {
          //If any Invalid rows to show again
          if (res?.some(i => i?.code === 200)) {
            setTableData(invalidRows);
            getNurse();
            toast.success(
              `${validRows?.length} ${
                validRows?.length > 1 ? 'records' : 'record'
              } updated successfully.`,
              {
                theme: 'colored'
              }
            );
            setLoading(false);
            setValidateTable(false);
            setFiles([]);
            setEmptyFields('');
          } else {
            setLoading(false);
            res?.map(i => {
              if (i?.error) {
                // console.error('error', i?.item, i?.error);
                toast.error(`${i?.error} - ${i?.item}`, {
                  theme: 'colored'
                });
              }
            });
          }
        })
        .catch(error => {
          setLoading(false);
          console.error('error', error);
        });
    }
  };

  let data = [
    {
      firstname: 'firstname',
      lastname: 'firstname',
      fulladdress: 'fulladdress',
      gender: 'gender',
      password: 'password',
      dateofbirth: moment().format('MM-DD-YYYY'),
      primarylicensetype: 'primarylicensetype',
      primarylicensemultiprivilege: 'yes',
      emailid: 'emailid',
      clinicalLicenseNumber: 'clinicalLicenseNumber',
      clinicalLicenseExpirationDate: moment().format('MM-DD-YYYY')
    }
  ];

  function convertToCSV(jsonData) {
    const separator = ',';
    const keys = Object.keys(jsonData[0]);
    const csvContent = [
      keys.join(separator),
      ...jsonData.map(item => {
        // Extract the 'FullAddress' and 'LicenseType' properties
        const { ...rest } = item;

        // Join the values of the other properties with the 'FullAddress' and 'LicenseType' values
        const values = keys.map(key => rest[key]);

        return values.join(separator);
      })
    ].join('\n');

    return csvContent;
  }

  const downloadCSVFile = data => {
    // Create a Blob object for the CSV data
    const blob = new Blob([data], { type: 'text/csv' });

    // Create a temporary URL for the Blob
    const url = URL.createObjectURL(blob);

    // Create a link element
    const link = document.createElement('a');
    link.href = url;
    link.download = `NurseUploadTemplate.csv`;

    // Trigger the download
    link.click();

    // Clean up the temporary URL
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <Card.Header>
        <Row className="flex-between-center">
          <Col sm={2}>
            <IconButton
              variant="falcon-default"
              size="sm"
              icon="download"
              transform="shrink-3"
              onClick={() => downloadCSVFile(convertToCSV(data))}
            >
              <span className="d-none d-sm-inline-block ms-1">Template</span>
            </IconButton>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body style={{ marginTop: -15 }}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div
            {...getRootProps({
              className: `${classNames('dropzone-area py-6', {
                'is-invalid': errors.uploadedFiles
              })}`
            })}
          >
            <input name="uploadedFiles" {...getInputProps()} />
            <p className="fs-8 text-primary mb-1">{location}</p>
            <Flex justifyContent="center">
              <img src={cloudUpload} alt="" width={25} className="me-2" />
              <p className="fs-9 mb-0 text-700">
                Drop your CSV or Excel files here
              </p>
            </Flex>
            <p className="fs-12 mb-0 text-700 mt-5">
              *FILE SHOULD INCLUDE ALL THE FIELDS
            </p>
            <div
              style={{ display: 'flex', flexWrap: 'wrap' }}
              className="mt-2 flex-1"
            >
              {field.map(item => (
                <Flex className="m-2 flex-1" key={item}>
                  <div>
                    <h6>{item}</h6>
                  </div>
                </Flex>
              ))}
            </div>
          </div>
          <div className="invalid-feedback">
            {errors.uploadedFiles?.message}
          </div>
          <div>
            {files.map(file => (
              <Flex
                alignItems="center"
                className="py-3 border-bottom btn-reveal-trigger"
                key={file.path}
              >
                <Flex
                  justifyContent="between"
                  alignItems="center"
                  className="ms-3 flex-1"
                >
                  <div>
                    <h6>{file.path}</h6>
                    <Flex className="position-relative" alignItems="center">
                      <p className="mb-0 fs-10 text-400 line-height-1">
                        <strong>{getSize(file.size)}</strong>
                      </p>
                    </Flex>
                  </div>
                </Flex>
                <CardDropdown>
                  <div className="py-2">
                    <Dropdown.Item
                      className="text-danger"
                      onClick={() => handleRemove(file.path)}
                    >
                      Remove
                    </Dropdown.Item>
                  </div>
                </CardDropdown>
              </Flex>
            ))}
          </div>
          <Button
            variant="falcon-primary"
            type="submit"
            className="me-2 mt-3"
            size="sm"
          >
            Submit File
          </Button>
        </Form>

        {tableData.length !== 0 && (
          <AdvanceTableProvider {...table}>
            <Card className="mb-3 mt-5">
              <>
                <Card.Body className="p-0">
                  <AdvanceTable
                    headerClassName="bg-200 text-nowrap align-middle"
                    rowClassName="align-middle white-space-nowrap"
                    tableProps={{
                      size: 'sm',
                      striped: true,
                      className: 'fs-10 mb-0 overflow-hidden'
                    }}
                  />
                </Card.Body>
                <Card.Footer>
                  <div className="mt-3">
                    <AdvanceTableFooter
                      rowInfo
                      navButtons
                      rowsPerPageSelection
                    />
                  </div>
                  <Flex>
                    <Button
                      variant="primary"
                      className="me-2 mt-3"
                      size="sm"
                      onClick={() => validateFields()}
                    >
                      Validate
                    </Button>
                    {validateTable && (
                      <Button
                        variant="success"
                        className="me-2 mt-3"
                        size="sm"
                        onClick={() => (loading ? null : upload())}
                      >
                        {loading ? 'Loading...' : 'Upload File'}
                      </Button>
                    )}
                  </Flex>
                </Card.Footer>
              </>
            </Card>
          </AdvanceTableProvider>
        )}
      </Card.Body>
    </Card>
  );
}

export default FileUploadNurse;
